import { useState, useRef, useEffect } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import { useNavigate } from 'react-router-dom';

import { useAuth } from "../../store/AuthContext/AuthContext";
import ApiManager from "../../api/ApiManager";

import BaseAppLayout from "../../layouts/BaseAppLayout/BaseAppLayout";
import Typography from "../../components/Typography/Typography";
import LibrarySearchBar from "../../components/LibrarySearchBar/LibrarySearchBar";
import Button from "../../components/Button/Button";
import DocumentCard from "../../components/DocumentCard/DocumentCard";
import Input from "../../components/Input/Input";
import FullPageModal from "../../components/FullPageModal/FullPageModal";
import { handleGeminiRequest } from "../Library/GeminiDocumentGeneration";
import { get } from "lodash";

/* -----------------------------------------------------
 * Main Library Page
 * ----------------------------------------------------- */
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
export default function Library() {
  // Auth & API
  const { isAuthenticated, user, getCookie } = useAuth();
  const api = new ApiManager();
  const location = useLocation();
  const id_token = getCookie("id_token");
  const navigate = useNavigate();


  // UI State
  const [searchBarInput, setSearchBarInput] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  const pdfCanvasRef = useRef(null);

  // Preview / Upload State
  const [link, setLink] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [fileType, setFileType] = useState(null); // Track the actual file type

  // Sources Data
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState([]);
  const [urls, setUrls] = useState([]);
  const [semantichs, setSemantichs] = useState([]);

  // Detect Mobile
  const isMobile = window.innerWidth < 768;

  /* -----------------------------------------------------
   * Fetch sources on mount (or when user changes)
   * ----------------------------------------------------- */
  useEffect(() => {
    if (!isAuthenticated || !user?.projectId || !user?.sub) {
      return;
    }

    setIsLoading(true);
    console.log("Fetching sources...");
    api
      .get(`/sources/fetch?project_id=${user.projectId}&user_sub=${user.sub}`, { headers: { Authorization: `Bearer ${id_token}` } })
      .then((res) => {
        let parsedData = res.data;
        console.log("✅ Fetched sources:", parsedData);

        if (typeof parsedData.body === "string") {
          parsedData = JSON.parse(parsedData.body);
        }

        const newDocuments = Array.isArray(parsedData.documents?.items)
          ? parsedData.documents.items
          : [];
        const newImages = Array.isArray(parsedData.images?.items)
          ? parsedData.images.items
          : [];
        const newUrls = Array.isArray(parsedData.urls?.items)
          ? parsedData.urls.items
          : [];
        const newSemantichs = Array.isArray(parsedData.semantic_scholar_results)
          ? parsedData.semantic_scholar_results
          : [];

        // Totale elementi
        const totalDocsCount =
          newDocuments.length +
          newImages.length +
          newUrls.length +
          newSemantichs.length;

        setDocuments(newDocuments);
        setImages(newImages);
        setUrls(newUrls);
        setSemantichs(newSemantichs);
      })
      .catch((err) => {
        console.error("Error fetching sources:", err);
        setDocuments([]);
        setImages([]);
        setUrls([]);
      })
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, user?.projectId, user?.sub, location.pathname]);

  /* -----------------------------------------------------
   * Poll thumbnail after uploads (to show final images)
   * ----------------------------------------------------- */
  const pollThumbnail = (fileName, type) => {
    if (!user?.projectId || !user?.sub) return;
    api
      .get(`/sources/fetch?project_id=${user.projectId}&user_sub=${user.sub}`, { headers: { Authorization: `Bearer ${getCookie("id_token")}` } })
      .then((res) => {
        let parsedData =
          typeof res.data.body === "string"
            ? JSON.parse(res.data.body)
            : res.data;

        // Safely fall back to empty arrays if .items is missing
        const docs = parsedData.documents?.items || [];
        const imgs = parsedData.images?.items || [];
        const links = parsedData.urls?.items || [];

        if (type === "documents") {
          const updatedItem = docs.find((doc) => doc.file_name === fileName);
          if (updatedItem?.thumbnail_url) {
            // Once we have the thumbnail, update state
            setDocuments((prev) =>
              prev.map((doc) =>
                doc.file_name === fileName ? updatedItem : doc
              )
            );
          } else {
            // Keep polling
            setTimeout(() => pollThumbnail(fileName, type), 2000);
          }
        } else if (type === "urls") {
          const updatedItem = links.find((url) => url.file_name === fileName);
          if (updatedItem?.thumbnail_url) {
            setUrls((prev) =>
              prev.map((url) =>
                url.file_name === fileName ? updatedItem : url
              )
            );
          } else {
            setTimeout(() => pollThumbnail(fileName, type), 2000);
          }
        } else if (type === "images") {
          const updatedItem = imgs.find((img) => img.file_name === fileName);
          // For images, we need image_url rather than thumbnail_url
          if (updatedItem?.image_url) {
            setImages((prev) =>
              prev.map((img) =>
                img.file_name === fileName ? updatedItem : img
              )
            );
          } else {
            setTimeout(() => pollThumbnail(fileName, type), 2000);
          }
        }
      })
      .catch((error) => {
        console.error("Error polling thumbnail:", error);
        // Retry after 2s
        setTimeout(() => pollThumbnail(fileName, type), 2000);
      });
  };


  // Start polling once the modal closes if we have a pending upload
  useEffect(() => {
    if (!isUploadModalOpen && pendingUpload) {
      pollThumbnail(pendingUpload.fileName, pendingUpload.type);
      setPendingUpload(null);
    }
  }, [isUploadModalOpen, pendingUpload]);

  /* -----------------------------------------------------
   * Handlers: Modal, Drag & Drop, File & Link
   * ----------------------------------------------------- */
  const handleModalOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setIsUploadModalOpen(false);
    setLink("");
    setUploadedFile(null);
    setPreviewData(null);
    setFileType(null); // Reset file type
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!e.dataTransfer.files?.length) return;

    const file = e.dataTransfer.files[0];
    const isImage = file.type === "image/png" || file.type === "image/jpeg";
    const isPdf = file.type === "application/pdf";

    // Store the actual file type
    setFileType(file.type);

    const previewImageUrl = isImage ? URL.createObjectURL(file) : "notfound";
    const fileSize =
      file.size < 1024 * 1024
        ? `${(file.size / 1024).toFixed(2)} KB`
        : `${(file.size / (1024 * 1024)).toFixed(2)} MB`;

    setUploadedFile(file);
    setPreviewData({
      type: "file",
      title: file.name,
      excerpt: fileSize,
      imageUrl: previewImageUrl,
      fileType: file.type // Store original file type in preview data
    });

    // For PDFs, generate preview
    if (isPdf) {
      renderPDFPreview(file);
    }

    e.dataTransfer.clearData();
  };

  // File Input
  const handleFileSelect = (e) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const isImage = file.type === "image/png" || file.type === "image/jpeg";
    const isPdf = file.type === "application/pdf";

    // Store the actual file type
    setFileType(file.type);

    setUploadedFile(file);

    const fileSize = file.size < 1024 * 1024
      ? `${(file.size / 1024).toFixed(2)} KB`
      : `${(file.size / (1024 * 1024)).toFixed(2)} MB`;

    setPreviewData({
      type: "file",
      title: file.name,
      excerpt: fileSize,
      imageUrl: isImage ? URL.createObjectURL(file) : "notfound",
      fileType: file.type // Store original file type in preview data
    });

    // For PDFs, generate preview
    if (isPdf) {
      renderPDFPreview(file);
    }
  };

  // Link Input
  const handleLinkChange = (e) => {
    setLink(e.target.value);
  };

  const handleLinkSubmit = async (linkValue) => {
    let normalizedLink = linkValue.trim();
    setIsLoading(true);

    setPreviewData({
      type: "link",
      title: normalizedLink,
      excerpt: "Caricamento in corso...",
      imageUrl: "notfound",
    });

    // Prepend https if missing
    if (!/^https?:\/\//i.test(normalizedLink)) {
      normalizedLink = `https://${normalizedLink}`;
    }

    try {
      console.log("Fetching link data...");
      console.log("Normalized link:", normalizedLink);

      const apiUrl = "https://api.peekalink.io/"
      const response = await api.post("", { link: normalizedLink }, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer sk_cfetcu3ew0ol0lpd09b4tayucb03aelt`,
        }
      }, apiUrl);

      // Access the data property of the response
      const data = response.data;
      console.log("API Response:", data); // Add this to debug

      const extractedData = {
        title: data.title || "No Title Found",
        description: data.description || "No Description Found",
        image: data.image?.original.url || "notfound",
      };
      console.log(extractedData);

      setPreviewData({
        type: "link",
        url: normalizedLink,
        title: extractedData.title,
        excerpt: extractedData.description,
        imageUrl: extractedData.image,
      });

    } catch (error) {
      console.error("Error fetching link data:", error);
      toast.error("Errore durante il caricamento dell'URL. Riprova più tardi.");
      handleModalClose();
    } finally {
      setLink("");
      setIsLoading(false);
    }
  };

  /* -----------------------------------------------------
   * Upload / Add Source
   * ----------------------------------------------------- */
  const handleAddSource = () => {

    // Make sure user & project are ready
    if (!user?.projectId || !user?.sub) {
      toast.error("Impossibile caricare: manca il projectId o il sub.");
      return;
    }

    // Adding a link
    if (previewData?.type === "link") {
      api
        .post("/url", {
          project_id: user.projectId,
          url: previewData.url,
          title: previewData.title,
          description: previewData.excerpt,
          image: previewData.imageUrl,
        }, { headers: { Authorization: `Bearer ${getCookie("id_token")}` } })
        .then((res) => {
          if (res.data.statusCode === 500) {
            console.error("Error posting link data:", res.data.body);
            toast.error("Errore durante il caricamento dell'URL.");
            return;
          }
          console.log(res);

          handleGeminiRequest(
            previewData.url,
            user.projectId,
            user.sub,
            previewData.url,
            null,
            id_token
          );

          setUrls((prev) => [
            {
              title: previewData.title,
              image: previewData.imageUrl,
            },
            ...prev,
          ]);
          setPendingUpload({ title: previewData.title, type: "urls" });
        })
        .catch((error) => console.error("Error posting link data:", error));

      setIsLoading(false);
      handleModalClose();
      return;
    }

    // Adding a file
    if (!uploadedFile) return;

    // Use the stored file type instead of relying on the preview data
    const actualFileType = fileType || uploadedFile.type;
    const isPDF = actualFileType === "application/pdf";
    const isImage = actualFileType === "image/png" || actualFileType === "image/jpeg";

    // Controllo formato supportato
    if (!isPDF && !isImage) {
      toast.error("Formato file non supportato. Carica solo PDF, PNG o JPEG.");
      return;
    }

    // Determina endpoint e tipo di contenuto
    const endpoint = isPDF ? "/documents" : "/images";
    const contentType = isPDF ? "application/pdf" : actualFileType;

    // Estrai l'estensione dal nome del file
    const fileExtension = uploadedFile.name.split(".").pop();

    // Avvia il caricamento
    setIsLoading(true);
    api
      .post(endpoint, {
        project_id: user.projectId,
        file_name: uploadedFile.name,
      }, { headers: { Authorization: `Bearer ${getCookie("id_token")}` } })
      .then((res) => {
        console.log(res)
        const body = JSON.parse(res.data.body);
        let file_id;

        if (isPDF) {
          file_id = `sources${endpoint}/${body?.document_id}.${fileExtension}`;
        } else {
          file_id = `sources${endpoint}/${body?.image_id}.${fileExtension}`;
        }
        const presignedUrl = body?.presigned_url?.replace(/\/$/, "");
        if (!presignedUrl) {
          console.error("Presigned URL not available:", body);
          toast.error("Errore nell'ottenere il presigned URL.");
          setIsLoading(false);
          return;
        }

        console.log("ora faccio il PUT")

        api
          .put(presignedUrl, uploadedFile, {
            headers: {
              "Content-Type": contentType,
              "x-amz-meta-project_id": user.projectId,
            },
          })
          .then((res) => {
            console.log(res)
            toast.success("File caricato con successo!");
            const now = new Date().toISOString();

            if (isPDF) {
              setDocuments((prev) => [
                {
                  file_name: uploadedFile.name,
                  thumbnail_url: null,
                  creation_date: now,
                },
                ...prev,
              ]);
              setPendingUpload({
                fileName: uploadedFile.name,
                type: "documents",
              });
            } else if (isImage) {
              setImages((prev) => [
                {
                  file_name: uploadedFile.name,
                  image_url: null,
                  creation_date: now,
                },
                ...prev,
              ]);
              setPendingUpload({
                fileName: uploadedFile.name,
                type: "images",
              });
            }

            // Pass the original file and its correct type to handleGeminiRequest
            handleGeminiRequest(
              uploadedFile,
              user.projectId,
              user.sub,
              file_id,
              isPDF ? "application/pdf" : actualFileType,
              id_token
            );

            setIsLoading(false);
            handleModalClose();
          })
          .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Errore nel caricamento del file.");
            setIsLoading(false);
          });
      })
      .catch((error) => {
        console.error("Error fetching presigned URL:", error);
        toast.error("Errore nel contattare il server.");
        setIsLoading(false);
      });
  };

  const renderPDFPreview = (file) => {
    const reader = new FileReader();
    reader.onload = async function () {
      try {
        // Create a temporary element to hold the rendering
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        // Load and render the PDF
        const pdfData = new Uint8Array(reader.result);
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        const page = await pdf.getPage(1);

        // Set dimensions at a good resolution
        const scale = 2.0;
        const viewport = page.getViewport({ scale });
        tempCanvas.width = viewport.width;
        tempCanvas.height = viewport.height;

        // Render to our temporary canvas
        const renderContext = {
          canvasContext: tempCtx,
          viewport: viewport,
        };

        await page.render(renderContext).promise;

        // Get the image as data URL
        const pdfImageUrl = tempCanvas.toDataURL('image/png');

        // Update the preview data to use this image URL instead of trying to render to canvas in the DOM
        setPreviewData(prevData => ({
          ...prevData,
          imageUrl: pdfImageUrl, // Override the imageUrl with our PDF preview
          isPdfPreview: true, // Flag to know this is a PDF preview
          fileType: "application/pdf" // Ensure we remember this is a PDF
        }));

      } catch (error) {
        console.error("❌ Error converting PDF to image:", error);
      }
    };
    reader.readAsArrayBuffer(file);
  };
  const handleSearch = (searchText) => {
    console.log("Searching for:", searchText);

    // Create proper URL search params
    const params = new URLSearchParams();
    params.set("query", searchText);

    navigate(
      {
        pathname: "/client/research",
        search: `?${params.toString()}`
      },
      { replace: true }
    );
  }



  /* -----------------------------------------------------
   * Render
   * ----------------------------------------------------- */
  return (
    <BaseAppLayout headerText="Libreria">
      <Toaster />
      <div className="w-full flex flex-col items-center justify-start px-6 lg:px-[120px] gap-5 md:gap-7 pt-10 pb-12 md:pb-20">
        <Typography
          size="28"
          desktopSize="48"
          family="serif"
          weight="sb"
          align="center"
        >
          Trova le fonti giuste.
          <br />
          Inizia ora!
        </Typography>

        <LibrarySearchBar
          value={searchBarInput}
          onChange={setSearchBarInput}
          onSearch={handleSearch}
        />
      </div>

      <div className="flex flex-col gap-12 pb-10">
        <LastResearches />

        {/* Library Section */}
        <div className="w-full h-fit flex flex-col px-6 lg:px-[120px] gap-6">
          <div className="flex flex-row justify-between items-center">
            <Typography
              size="28"
              desktopSize="32"
              family="serif"
              weight="sb"
              align="center"
            >
              La tua libreria
            </Typography>
            {isMobile ? (
              <Button
                type="bw"
                size="md"
                icon={<Plus size={16} />}
                onClick={handleModalOpen}
              />
            ) : (
              <Button
                text="Aggiungi"
                type="bw"
                size="sm"
                ricon={<Plus size={16} />}
                onClick={handleModalOpen}
              />
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-12 gap-6">
            {isLoading ? (
              // Loading skeleton
              [...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="col-span-full md:col-span-6 lg:col-span-4 xl:col-span-3 rounded-md bg-gray-100 animate-pulse aspect-[4/3]"
                />
              ))
            ) : documents.length === 0 &&
              images.length === 0 &&
              urls.length === 0 ? (
              // No files found
              <>
                <div className="col-span-full flex justify-center">
                  <div className="flex items-center justify-center w-full">
                    <img
                      src="/assets/images/illustrations/no_files_found.png"
                      alt="No files found"
                      className="w-80"
                    />
                  </div>
                </div>
              </>
            ) : (
              // Render each card type
              <>
                {semantichs.map((semantich, index) => (
                  <DocumentCard
                    isScholar={true}
                    isListed={true}
                    key={semantich.paper_id}
                    id={semantich.paper_id}
                    awsId={semantich.id}
                    title={semantich.title}
                    siteUrl={semantich.url}
                    isRatio={isMobile ? false : true}
                    isMobileLibrary={isMobile}
                    description={
                      semantich.tldr !== ""
                        ? semantich.tldr
                        : semantich.abstract
                    }
                    authors={semantich.authors}
                    hasPdf={true}
                    date={semantich.year}
                    citations={semantich.citationCount}
                  />
                ))}
                {urls.map((urlItem, index) => (
                  <DocumentCard
                    key={urlItem.url}
                    id={urlItem.title}
                    title={urlItem.title}
                    siteUrl={urlItem.url}
                    isRatio={isMobile ? false : true}
                    isMobileLibrary={isMobile}
                    isScholar={false}
                    imageUrl={urlItem.image}
                  />
                ))}
                {documents.map((doc, index) => (
                  <DocumentCard
                    key={doc.id}
                    id={doc.id}
                    title={doc.file_name}
                    isRatio={isMobile ? false : true}
                    isMobileLibrary={isMobile}
                    isScholar={false}
                    imageUrl={doc.thumbnail_url}
                    placeholder={!doc.thumbnail_url}
                  />
                ))}
                {images.map((img, index) => (
                  <DocumentCard
                    key={img.id}
                    id={img.id}
                    title={img.file_name}
                    isRatio={isMobile ? false : true}
                    isMobileLibrary={isMobile}
                    isScholar={false}
                    imageUrl={img.image_url}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Full Page Modal for Uploads */}
      <FullPageModal
        open={isUploadModalOpen}
        onClose={handleModalClose}
        title="Aggiungi una fonte"
      >
        {previewData ? (
          <div className="w-full h-full flex flex-col md:flex-row gap-4 overflow-hidden relative">
            {/* Preview Image */}
            {previewData.imageUrl !== "notfound" ? (
              <div className="flex-grow flex items-start justify-center bg-gray-100 border-2 border-gray-200 rounded-[12px] px-6 pt-6 overflow-hidden md:aspect-none aspect-[4/3]">
                <img
                  src={previewData.imageUrl}
                  alt={previewData.title}
                  className={`w-full h-full object-cover object-top rounded-t-[8px] ${previewData.isPdfPreview ? '' : ''}`}
                />
              </div>
            ) : (
              <div className="flex-grow flex items-start justify-center bg-gray-100 border-2 border-gray-200 rounded-[12px] px-6 pt-6 overflow-hidden md:aspect-none aspect-[4/3]">
                <div className="bg-gray-200 animate-pulse h-full w-full rounded-t-[8px]" />
              </div>
            )}

            {/* Preview Info */}
            <div className="w-full md:w-6/12 flex flex-col justify-between h-full">
              <div className="flex flex-col gap-3 mb-5 md:mb-0">
                <Typography size="20" desktopSize="28" weight="sb" truncate>
                  {previewData.title}
                </Typography>
                <Typography size="16" color="gray-600" weight="rg">
                  {previewData.excerpt}
                </Typography>
              </div>
              <Button
                type="color"
                size="md"
                width="full"
                ricon={isLoading ? null : <Plus size={20} />}
                onClick={handleAddSource}
                disabled={isLoading}
                text={isLoading ? "Caricamento in corso..." : "Aggiungi fonte"}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full gap-6">
            {/* Drag & Drop area */}
            <div
              className={`grow rounded-[12px] border-2 md:p-4 py-12 flex items-center justify-center transition-all duration-200 ${isDragging
                ? "border-accent outline outline-[3px] outline-accent-light"
                : "border-gray-200"
                } relative overflow-hidden`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-center flex flex-col items-center gap-4">
                <Typography
                  size="20"
                  desktopSize="28"
                  weight="sb"
                  color={isDragging ? "accent" : "gray-600"}
                  align="center"
                >
                  Trascina un file
                  <br />
                  qua sopra
                </Typography>
                <div className="hidden md:block">
                  <Typography size="16" color={isDragging ? "accent" : "gray-600"}>
                    o{" "}
                    <span
                      className={`underline cursor-pointer ${isDragging ? "accent" : "gray-600"
                        } hover:text-gray-800`}
                      onClick={handleBrowseClick}
                    >
                      sfoglia tra i tuoi file.
                    </span>
                  </Typography>
                </div>
                {/* Mobile click area */}
                <div
                  className="md:hidden absolute w-full h-full top-0 flex justify-center items-center cursor-pointer"
                  onClick={handleBrowseClick}
                />
                <input
                  type="file"
                  accept=".pdf, .png, .jpg, .jpeg"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
              <img
                src="/assets/images/illustrations/file_management.png"
                alt="Drag and drop"
                className="hidden md:block h-56 absolute -bottom-12 left-0"
              />
            </div>

            {/* Link input */}
            <div className="flex flex-col gap-3">
              <Typography size="20" weight="md">O aggiungi un link:</Typography>
              <Input
                placeholder="ex: https://en.wikipedia.org/wiki/COVID-19"
                value={link}
                onChange={handleLinkChange}
                onSubmit={handleLinkSubmit}
              />
            </div>
          </div>
        )}

      </FullPageModal>
    </BaseAppLayout >
  );
}

/* -----------------------------------------------------
 * LastResearches Component
 * ----------------------------------------------------- */

function LastResearches() {

  const isMobile = window.innerWidth < 768;
  const { getCookie } = useAuth();
  const projectId = getCookie("project_id");
  const id_token = getCookie("id_token");
  const { user } = useAuth();
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getChats = () => {
    const api = new ApiManager();
    console.log("Retrieving chats...");

    api.get(`/search/get?project_id=${projectId}`, { headers: { Authorization: `Bearer ${id_token}` } })
      .then((response) => {
        console.log(response)
        if (response.data && response.data.history) {
          try {
            const parsedBody = typeof response.data.history === 'string'
              ? JSON.parse(response.data.history)
              : response.data.history;

            console.log("Parsed Body:", parsedBody);
            setSearchData(parsedBody || []);  // Assumendo che history sia l'array da mappare
          } catch (error) {
            console.error("Errore nel parsing del corpo della risposta:", error);
            setSearchData([]); // fallback
          }
        } else {
          console.error("Response body is missing or malformed.");
          setSearchData([]); // fallback
        }
      })
      .catch((error) => {
        console.error("Errore nella chiamata API:", error);
        setSearchData([]); // fallback in caso di errore
      })
      .finally(() => {
        setLoading(false); // Imposta loading a false dopo che la risposta è arrivata
      });
  };

  useEffect(() => {
    // Chiamata per ottenere le chat
    getChats();
  }, [user?.projectId]);

  // Ordinare le chat dalla più recente alla più vecchia in base alla CreationDate
  const sortedData = searchData.sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));

  // Mostrare solo le prime 10 chat
  const displayedData = sortedData.slice(0, 10);

  return (
    (displayedData.length > 0 && (
      <div className="flex flex-col gap-6">
        <div className="w-full h-fit flex flex-col px-6 lg:px-[120px] gap-6">
          <Typography size="28" desktopSize="32" family="serif" weight="sb">
            Le tue ricerche
          </Typography>
        </div>
        <div className="w-full">
          <ScrollContainer className="w-full scrollbar-hidden" horizontal>
            <div className="w-fit flex gap-3 px-6 lg:px-[120px]">
              {loading ? (
                <>
                  <div className="w-60 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                  <div className="w-44 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                  <div className="w-52 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                  <div className="w-44 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                  <div className="w-60 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                  <div className="w-52 bg-gray-100 animate-pulse rounded-[6px] h-16" />
                </>
              ) : displayedData.length > 0 ? (
                <>
                  {/* Mappare le chat mostrate, incluse le prime 10 */}
                  {displayedData.map((item, index) => (
                    <Button
                      key={index}
                      width="fit"
                      text={item.title} // Utilizziamo il titolo dalla risposta dell'API
                      type="bw"
                      size={isMobile ? "sm" : "md"}
                      onClick={() => navigate(`/client/research?chat=${item.chat_id}`)}
                    />
                  ))}

                  {/* Se ci sono più di 10 chat, mostrare il link "Mostra tutte le chat" */}
                  {sortedData.length > 10 && (
                    <Button
                      key={999}
                      width="fit"
                      text="Mostra tutte le chat"
                      type="bw"
                      ricon={<ArrowRight size={16} />}
                      size={isMobile ? "sm" : "md"}
                      onClick={() => {
                        window.location.href = "/client/research/chats";
                      }}
                    />
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </ScrollContainer>
        </div>
      </div>
    )
    )
  );
}